import React, {RefObject} from 'react';
import {Container} from '@inlet/react-pixi';
import {ButtonWithIcon, FlexBoxComponent, RichText} from '@gongxigames/framework/core/fragments/react';
import {Container as PixiContainer, Rectangle} from 'pixi.js';
import {CoreDI, lazyInject, UpdateManager} from '@gongxigames/framework/core';
import {SystemPopupBackground} from '../index';

const WIDTH = 1456;
const HeadDefaultHeight = 160;
const CLOSE_BUTTON_Y_POSITION = 40;
const CLOSE_BUTTON_X_OFFSET = 140;

type ContentSize = {
    width: number;
    height: number;
};

type SystemPopupProps = {
    title: string | JSX.Element;
    titleEnabled?: boolean;
    closeButtonEnabled?: boolean;
    onClickClose?: () => void;
    gapBetweenElements?: number;
    onUpdatedContentSize?: (contentSize: ContentSize) => void;
    width?: number;
    height?: number;
};

type SystemPopupState = {
    contentSize: ContentSize;
};

export class SystemPopupView extends React.Component<SystemPopupProps, SystemPopupState> {
    public state: SystemPopupState = {
        contentSize: {width: WIDTH, height: 0}
    };

    protected content: RefObject<PixiContainer> = React.createRef<PixiContainer>();

    @lazyInject(CoreDI.UpdateManager)
    protected updateManager!: UpdateManager;

    protected checkUpdateContentSizeCallback = this.checkUpdateContentSize.bind(this);

    protected checkUpdateContentSize(): void {
        if (this.content.current) {
            const contentBoundsRect = this.content.current._bounds.getRectangle(new Rectangle);
            const contentSize: ContentSize = {
                width: Math.max(WIDTH, Math.trunc(contentBoundsRect.width)),
                height: Math.trunc(contentBoundsRect.height)
            };

            if (contentSize.width === this.state.contentSize.width && contentSize.height === this.state.contentSize.height) {
                return;
            }

            this.setState({
                contentSize: {
                    width: contentSize.width,
                    height: contentSize.height
                }
            });

            if (this.props.onUpdatedContentSize) {
                this.props.onUpdatedContentSize({
                    width: contentSize.width ,
                    height: contentSize.height
                });
            }
        }
    }

    public componentDidMount(): void {
        this.updateManager.addUpdateFunction(this.checkUpdateContentSizeCallback);
    }

    public componentWillUnmount(): void {
        this.updateManager.removeUpdateFunction(this.checkUpdateContentSizeCallback);
    }

    protected getTitle(): JSX.Element {
        if (typeof this.props.title === 'string') {
            return <Container>
                <RichText id={this.props.title} style={'SystemPopupTitle'} resolution={1}/>
            </Container>;
        }

        return <Container>{this.props.title}</Container>;
    }

    public render(): JSX.Element {
        const {height: contentHeight, width: contentWidth} = this.state.contentSize;
        const {width = 0, height = 0} = this.props;
        const {
            titleEnabled = true,
            closeButtonEnabled,
            onClickClose = () => {}
        } = this.props;
        const HeadHeight = titleEnabled ? HeadDefaultHeight : 0;
        const bodyHeight = Math.max(height, contentHeight) || 0;
        const bodyWidth = Math.max(width, contentWidth) || 0;
        const contentY = HeadHeight + (bodyHeight - contentHeight) * 0.5;

        return (
            <Container interactive={true} >
                <SystemPopupBackground width={bodyWidth} headHeight={HeadHeight} bodyHeight={bodyHeight} titleEnabled={titleEnabled}/>
                <Container >
                    {titleEnabled && <FlexBoxComponent name={'SystemPopupView-title'}
                        alignItems={'center'} justifyContent={'center'} containerWidth={bodyWidth} containerHeight={HeadHeight}>
                        {this.getTitle()}
                    </FlexBoxComponent>}
                    <ButtonWithIcon
                        hitArea={new Rectangle(-100, -100, 240, 240)}
                        visible={closeButtonEnabled}
                        y={CLOSE_BUTTON_Y_POSITION}
                        x={bodyWidth - CLOSE_BUTTON_X_OFFSET}
                        onClick={onClickClose}
                        icon={{
                            up: 'close_button_normal',
                            down: 'close_button_pressed'
                        }}
                    />
                </Container>
                <Container ref={this.content} y={contentY}>
                    {this.props.children}
                </Container>
            </Container>
        );
    }
}
